// frontend/src/api/auth.ts
import apiClient from './index'
import type {
  LoginRequest, LoginResponse, User,
  RegisterPayload, RegisterResponse,
  SendCallResponse, PhoneStatusResponse, VerifyCodeResponse,
} from '@/types'

export const authApi = {
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const response = await apiClient.post<LoginResponse>('/auth/login', credentials)
    return response.data
  },

  loginByPhone: async (phone: string, password: string): Promise<LoginResponse> => {
    const response = await apiClient.post<LoginResponse>('/auth/login', { phone, password })
    return response.data
  },

  loginByToken: async (verifiedToken: string): Promise<LoginResponse> => {
    const response = await apiClient.post<LoginResponse>('/auth/login', { verified_token: verifiedToken })
    return response.data
  },

  refresh: async (refreshToken: string): Promise<{ access_token: string; refresh_token: string }> => {
    const response = await apiClient.post('/auth/refresh', { refresh_token: refreshToken })
    return response.data
  },

  me: async (): Promise<User> => {
    const response = await apiClient.get<User>('/auth/me')
    return response.data
  },

  updateProfile: async (data: {
    fullName?: string
    currentPassword?: string
    newPassword?: string
    phone?: string
    verifiedToken?: string
    messengerType?: 'telegram' | 'whatsapp' | null
    messengerHandle?: string | null
  }): Promise<User> => {
    const payload: Record<string, unknown> = {}
    if (data.fullName !== undefined) payload.full_name = data.fullName
    if (data.currentPassword !== undefined) payload.current_password = data.currentPassword
    if (data.newPassword !== undefined) payload.new_password = data.newPassword
    if (data.phone !== undefined) payload.phone = data.phone
    if (data.verifiedToken !== undefined) payload.verified_token = data.verifiedToken
    if (data.messengerType !== undefined) payload.messenger_type = data.messengerType
    if (data.messengerHandle !== undefined) payload.messenger_handle = data.messengerHandle
    const response = await apiClient.put<User>('/auth/me', payload)
    return response.data
  },

  changePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
    await apiClient.post('/auth/change-password', { current_password: currentPassword, new_password: newPassword })
  },

  logout: async (refreshToken?: string | null): Promise<void> => {
    await apiClient.post('/auth/logout', refreshToken ? { refresh_token: refreshToken } : undefined)
  },

  register: async (data: RegisterPayload): Promise<RegisterResponse> => {
    const response = await apiClient.post<RegisterResponse>('/auth/register', data)
    return response.data
  },

  sendCall: async (phone: string, mode: 'wait_call' | 'flash_call' = 'wait_call'): Promise<SendCallResponse> => {
    const response = await apiClient.post<SendCallResponse>('/auth/phone/send-call', { phone, mode })
    return response.data
  },

  phoneStatus: async (sessionId: string): Promise<PhoneStatusResponse> => {
    const response = await apiClient.get<PhoneStatusResponse>('/auth/phone/status', {
      params: { session_id: sessionId },
    })
    return response.data
  },

  verifyCode: async (sessionId: string, code: string): Promise<VerifyCodeResponse> => {
    const response = await apiClient.post<VerifyCodeResponse>('/auth/phone/verify', {
      session_id: sessionId,
      code,
    })
    return response.data
  },

  skipPhone: async (): Promise<void> => {
    await apiClient.post('/auth/phone/skip')
  },

  getConfig: async (): Promise<{ phone_verification_required: boolean }> => {
    const response = await apiClient.get<{ phone_verification_required: boolean }>('/auth/config')
    return response.data
  },
}
